import { Button } from '@/components/base/button'
import { Dialog, DialogActions, DialogBody, DialogTitle, ShowProps } from '@/components/base/dialog'
import { Divider } from '@/components/base/divider'
import { useEffect, useState } from 'react'
import { ClientQuery } from './__generated__/show_query'
import { ClientQuery as Query } from './show_query'
import { useQuery } from '@apollo/client'
import { Inline as Loading } from '@/components/loading'
import { Inline as Error } from '@/components/error'
import { Subheading } from '@/components/base/heading'
import { Badge as ClientTypeBadge } from '@/components/client_type/badge'
import { EnvelopeIcon, MapPinIcon, PhoneIcon } from '@heroicons/react/24/solid'
import { Accordion } from '@/components/accordion'
import { DeleteDialog } from '@/components/delete_dialog'
import { Mutation as DeleteMutation } from './delete_mutation'
import { Query as TableQuery } from '@/components/client/table_query'
import { EditDialog } from './edit_dialog'
import { Table as EventTable } from '@/components/event/table'
import { Table as DocumentTable } from '@/components/document/table'
import { formatCurrency } from '@/helpers/formatCurrency'

type Client = ClientQuery['clients']['nodes'][0]

export const ShowDialog = ({ id, open, setOpen }: ShowProps) => {
  const [openEdit, setOpenEdit] = useState(false)
  const [openDelete, setOpenDelete] = useState(false)
  const [client, setClient] = useState<Client | null>(null)

  const { data, loading, error } = useQuery<ClientQuery>(Query, { variables: { filters: { idEq: id } } })

  useEffect(() => {
    if (!data || !id) { return }

    setClient(data.clients.nodes[0])
  }, [id, data])

  const onEdit = () => {
    setOpenEdit(true)
  }
  const onDelete = () => {
    setOpenDelete(true)
  }

  const onDeleteSuccess = () => {
    setOpenDelete(false)
    setOpen(false)
  }

  return (
    <>
      <Dialog open={open} size="7xl" onClose={() => setOpen(false)}>
        <DialogTitle>
          { loading || !client
            ? <Loading />
            : <div className="flex flex-row justify-between">
              <div>
                <span className="flex flex-row gap-4 items-center pb-6">
                  { client.organisationName }
                  <ClientTypeBadge clientType={client.clientType} />
                </span>
                { client.address && <span className="flex flex-row gap-2 items-center">
                  <MapPinIcon className="h-6 w-6" />
                  <Subheading>{ client.address }</Subheading>
                </span>}
                { client.email && <span className="flex flex-row gap-2 items-center">
                  <EnvelopeIcon className="h-6 w-6" />
                  <Subheading>{ client.email }</Subheading>
                </span>}
                { client.phone && <span className="flex flex-row gap-2 items-center">
                  <PhoneIcon className="h-6 w-6" />
                  <Subheading>{ client.phone }</Subheading>
                </span>}
              </div>
            </div> }
        </DialogTitle>
        <DialogBody>
          { error && <Error message={error.message} />}
          { client && <div className="flex flex-col gap-4">
            <div className="grid grid-cols-4 items-center align-center" />
            <Divider />
            <Accordion title="Events">
              <EventTable filters={{ clientIdEq: client.id }} hiddenColumns={['Client']} pageSize={20} fixedHeight />
            </Accordion>
            <Accordion title="Documents">
              <DocumentTable filters={{ subjectIdEq: client.id }} pageSize={20} fixedHeight />
            </Accordion>
            <Accordion title="Stats">
              <div>
                <p>Lifetime event count: {client.eventsCount}</p>
                <p>Total revenue: {formatCurrency('gbp', client.totalRevenue)}</p>
                <p>Average revenue per event: { formatCurrency('gbp', client.totalRevenue / client.eventsCount)} </p>
                <p>Total discounts provided: { formatCurrency('gbp', client.totalEventDiscounts)}</p>
                <p>Average event length:  { (client.eventLengthTotal / client.eventsCount).toFixed(2)} days</p>
              </div>
            </Accordion>
          </div> }
        </DialogBody>
        <DialogActions>
          { client && <Button color="amber" onClick={onEdit}>Edit</Button> }
          { client?.eventsCount == 0 && <Button color="red" onClick={onDelete}>Delete</Button> }
        </DialogActions>
      </Dialog>
      { client && <EditDialog id={client.id} open={openEdit} setOpen={setOpenEdit} /> }
      {
        client?.eventsCount == 0 &&
          <DeleteDialog
            id={client.id}
            mutation={DeleteMutation}
            open={openDelete}
            refetchQueries={[TableQuery]}
            setOpen={setOpenDelete}
            onSuccess={onDeleteSuccess}
          />
      }
    </>
  )
}
